import { create } from 'zustand';
import { getSubscriptionPlans } from '../api/generated/subscription-plans/subscription-plans';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import i18n from '@/i18n/config';
import type {
  CreateSubscriptionPlanRequest,
  SubscriptionPlanDto,
  UpdateSubscriptionPlanRequest,
} from '../api/types/admin';

const {
  getApiV1SubscriptionPlans,
  postApiV1SubscriptionPlans,
  putApiV1SubscriptionPlansId,
  deleteApiV1SubscriptionPlansId,
  patchApiV1SubscriptionPlansIdActivate,
  patchApiV1SubscriptionPlansIdDeactivate,
} = getSubscriptionPlans();

const t = (key: string) => i18n.t(key);

interface SubscriptionPlanState {
  plans: SubscriptionPlanDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  create: (data: CreateSubscriptionPlanRequest) => Promise<string | null>;
  update: (id: string, data: UpdateSubscriptionPlanRequest) => Promise<boolean>;
  setActive: (id: string, isActive: boolean) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
}

export const useSubscriptionPlanStore = create<SubscriptionPlanState>((set, get) => ({
  plans: [],
  loading: false,
  saving: false,
  error: null,

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const response = await getApiV1SubscriptionPlans();
      const sorted = [...(response ?? [])].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
      );
      set({ plans: sorted, loading: false });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  create: async (data) => {
    set({ saving: true });
    try {
      const created = await postApiV1SubscriptionPlans(data);
      set({ saving: false });
      await get().fetchAll();
      useToastStore.getState().addToast(t('admin:plans.messages.created'), 'success');
      return created?.id ?? null;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return null;
    }
  },

  update: async (id, data) => {
    set({ saving: true });
    try {
      const updated = await putApiV1SubscriptionPlansId(id, data);
      set((s) => ({
        plans: s.plans
          .map((p) => (p.id === id ? { ...p, ...updated } : p))
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:plans.messages.updated'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  setActive: async (id, isActive) => {
    set({ saving: true });
    try {
      const updated = isActive
        ? await patchApiV1SubscriptionPlansIdActivate(id)
        : await patchApiV1SubscriptionPlansIdDeactivate(id);
      set((s) => ({
        plans: s.plans.map((p) => (p.id === id ? { ...p, ...updated } : p)),
        saving: false,
      }));
      useToastStore.getState().addToast(
        isActive ? t('admin:plans.messages.activated') : t('admin:plans.messages.deactivated'),
        'success',
      );
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  remove: async (id) => {
    set({ saving: true });
    try {
      await deleteApiV1SubscriptionPlansId(id);
      set((s) => ({
        plans: s.plans.filter((p) => p.id !== id),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:plans.messages.deleted'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },
}));
